import { Dispatch, SetStateAction, useRef, useState } from "react";
import axios, { type AxiosProgressEvent } from "axios";
import $api from "@/frontend/shared/api/axios";
import { AddCompaniesUploadModal } from "@/frontend/features/Company/ui/AddCompaniesUploadModal/AddCompaniesUploadModal";
import { TCompanyInfo } from "@/frontend/pages/DashboardPage/model/types/types";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { flushSync } from "react-dom";
import { EContent } from "@/frontend/components/DashboardHeader/types/types";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
import { PlusIcon } from "@/frontend/shared/assets/icons";
import { SquareNormalButton } from "@/frontend/shared/ui/Button/SquareNormalButton/SquareNormalButton";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { EDataStatus } from "../../model/types/types";

function handleBeforeUnload(e: any) {
  e.preventDefault();
  e.returnValue = "You have an ongoing file upload. Are you sure you want to leave?";
  return e.returnValue;
}

const AZURE_UPLOAD_PROGRESS_CAP = 100;
export function AddCompanies({
  triggerRefetch,
  triggerRefetchAll,
  setBulkDataInfo,
  setFileUploadProgresses,
  setDashboardContent,
  isWhiteButton,
  stageOptions,
  buttonId
}: {
  triggerRefetch: () => void;
  triggerRefetchAll: () => void;
  setBulkDataInfo: Dispatch<SetStateAction<TCompanyInfo[]>>;
  setFileUploadProgresses: Dispatch<SetStateAction<Record<string, number>>>;
  setDashboardContent: Dispatch<SetStateAction<EContent>>;
  isWhiteButton?: boolean;
  stageOptions: { label: string; value: string }[];
  buttonId?: string;
}) {
  const [isUploadModelOpen, setIsUploadModelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isFirstFetch = useRef(true);
  const isPipelineSection = localStorage.getItem(ELOCAL_STORAGE_KEY.CONTENT)?.replace(/"/g, "") === EContent.PIPELINE;

  async function uploadFiles(data: FileList, stageId?: string, tagNames?: string[]) {
    const filesArray = Array.from(data);

    window.addEventListener("beforeunload", handleBeforeUnload);

    setIsLoading(true);

    const response = await $api.get<{ data: { url: string; token: string } }>("/me/uploads/bulk/url");
    const url = response?.data?.data?.url;
    const token = response?.data?.data?.token;
    if (!url || !token) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      snackbarConstructor("Failed to upload file/s. Try again later or contact administrator.", "error");
      return;
    }

    if (!isPipelineSection) {
      setDashboardContent(EContent.PIPELINE);
    }

    const _fileUploadProgresses: Record<string, number> = {};
    for (const file of filesArray) {
      _fileUploadProgresses[file.name] = 0;
    }

    setFileUploadProgresses(_fileUploadProgresses);

    const unixTimestamp = new Date().getTime();
    const promises = filesArray.map(async (file, idx) => {
      const filename = file.name;
      const cleanFilename = decodeURIComponent(filename)
        .replace(/[#()]/g, "")
        .replace(/\s+/g, "-")
        .trim()
        .toLowerCase();
      const newFilename = `${unixTimestamp}-${cleanFilename}`;
      const fileUrl = `${url}/${encodeURIComponent(newFilename)}`;
      const sasUploadUrl = `${fileUrl}?${token}`;

      const azureResponse = await axios.put(sasUploadUrl, file, {
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          setFileUploadProgresses((progressObj) => {
            const percentCompleted = progressEvent?.total
              ? Math.round((progressEvent.loaded * AZURE_UPLOAD_PROGRESS_CAP) / progressEvent.total)
              : 1; // frontend illusion - J

            progressObj[filename] = percentCompleted;
            return progressObj;
          });
        }
      });

      if (azureResponse.status !== 201) {
        const parsingCompanyInfo = {
          name: filename,
          id: -idx, // this ensures uniqueness since mysql IDs are natural numbers - J
          parsingStatus: EDataStatus.error,
          parsingProgress: 0,
          parsingMessage: `Failed to upload ${filename}`
        };

        setBulkDataInfo((prev) => [...prev, parsingCompanyInfo]);
        setFileUploadProgresses((progressObj) => {
          delete progressObj?.[filename];
          return progressObj;
        });
        reportErrorToSentry(
          `Failed to upload ${filename} with Azure shared access signature`,
          `/portfolio-companies/parse/url`
        );
        return;
      }

      // We delete here because the parsing progress data
      // is now owned by the backend. UI trickery! - J
      flushSync(() => {
        setFileUploadProgresses((progressObj) => {
          delete progressObj?.[filename];
          return progressObj;
        });
      });

      return $api
        .post(
          "/portfolio-companies/parse/url",
          { fileUrl, stageId, tagNames },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json"
            }
          }
        )
        .then((res) => {
          const parsingCompanyInfo = {
            name: filename,
            id: res.data.data.newPortfolioCompanyId,
            parsingStatus: EDataStatus.parsing,
            parsingProgress: res?.data?.data?.parsingProgress || 0,
            parsingMessage: res?.data?.parsingMessage ?? ""
          };

          setBulkDataInfo((prev) => [...prev, parsingCompanyInfo]);

          if (isFirstFetch.current && idx === filesArray.length - 1) {
            isFirstFetch.current = false;
            triggerRefetchAll();
            setIsLoading(false);
            setIsUploadModelOpen(false);
          }
        })
        .catch((error) => {
          const axiosError = error as AxiosError<{ message: any }>;

          const parsingCompanyInfo = {
            name: filename,
            id: 0,
            parsingStatus: EDataStatus.error,
            parsingProgress: 0,
            parsingMessage: axiosError?.response?.data.message
          };
          setBulkDataInfo((prev) => [...prev, parsingCompanyInfo]);
          reportErrorToSentry(
            axiosError?.response?.data.message ?? "Something went wrong",
            `/portfolio-companies/parse`
          );
        });
    });

    await Promise.all(promises);

    window.removeEventListener("beforeunload", handleBeforeUnload);

    setIsUploadModelOpen(false);
    triggerRefetch();
    triggerRefetchAll();
  }

  const isMobile = useScreenWidth() <= 768;

  return (
    <>
      <SquareNormalButton
        size="large"
        variant={isWhiteButton ? "secondary" : "primary"}
        onClick={() => setIsUploadModelOpen(true)}
        id={buttonId}
      >
        <PlusIcon />
        {!isMobile && "Add Companies"}
      </SquareNormalButton>

      <AddCompaniesUploadModal
        isOpen={isUploadModelOpen}
        onClose={() => setIsUploadModelOpen(false)}
        uploadFiles={uploadFiles}
        stageOptions={stageOptions}
      />
    </>
  );
}
